import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchServiceAgreements,
  fetchServiceAgreement,
  updateServiceAgreement,
  createServiceAgreement,
  deleteServiceAgreement,
  listUpdateServiceAgreement,
} from "../../api/service/agreement";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  serviceAgreement: {},
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(
    normalizeError(err, { operationKey })
  );
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

/** Prefer API/field message; else operation-specific Norwegian fallback. */
function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteServiceAgreementAsync = createAsyncThunk(
  "serviceAgreements/deleteServiceAgreementAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteServiceAgreement(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_service_agreement");
    }
  }
);

export const createserviceAgreementAsync = createAsyncThunk(
  "serviceAgreements/createserviceAgreementAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await createServiceAgreement(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_service_agreement");
    }
  }
);

export const updateServiceAgreementAsync = createAsyncThunk(
  "serviceAgreements/updateServiceAgreementAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await updateServiceAgreement(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_service_agreement");
    }
  }
);

export const getServiceAgreementsAsync = createAsyncThunk(
  "serviceAgreements/getServiceAgreementsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchServiceAgreements(controller);
      const status = response?.response?.status ?? response?.status;
      if (isHttpFailureStatus(status) || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "list_service_agreements");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_service_agreements");
    }
  }
);

export const getServiceAgreementAsync = createAsyncThunk(
  "serviceAgreements/getServiceAgreementAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchServiceAgreement(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_service_agreement");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_service_agreement");
    }
  }
);

export const listUpdateServiceAgreementAsync = createAsyncThunk(
  "serviceAgreements/listUpdateServiceAgreementAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await listUpdateServiceAgreement(payload);
      const status = response?.response?.status ?? response?.status;
      if (response?.error || isHttpFailureStatus(status) || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "list_update_service_agreements"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(
        rejectWithValue,
        err,
        "list_update_service_agreements"
      );
    }
  }
);

const serviceAgreementSlice = createSlice({
  name: "serviceAgreements",
  initialState,
  reducers: {
    clearServiceAgreementError(state) {
      state.error = false;
      state.errors = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServiceAgreementsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getServiceAgreementsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload?.data ?? [];
        state.totalCount = action.payload?.totalCount ?? 0;
        state.currentCount = action.payload?.currentCount ?? 0;
        state.newCount = action.payload?.newCount ?? 0;
      })
      .addCase(getServiceAgreementsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente serviceavtaler."
        );
      })
      .addCase(updateServiceAgreementAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateServiceAgreementAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.payload?.id;
          const index = draftState.data.findIndex((type) => type.id === id);
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateServiceAgreementAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere serviceavtalen."
        );
      })
      .addCase(createserviceAgreementAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createserviceAgreementAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(createserviceAgreementAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette serviceavtalen."
        );
      })
      .addCase(deleteServiceAgreementAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteServiceAgreementAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.meta?.arg?.id;
          if (id != null) {
            draftState.data = draftState.data.filter((row) => row.id !== id);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteServiceAgreementAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette serviceavtalen."
        );
      })
      .addCase(getServiceAgreementAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getServiceAgreementAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.serviceAgreement = action.payload?.data || action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getServiceAgreementAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente serviceavtalen."
        );
      })
      .addCase(listUpdateServiceAgreementAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(listUpdateServiceAgreementAsync.fulfilled, (state, action) => {
        state.serviceAgreement = action.payload;
        state.isLoading = false;
      })
      .addCase(listUpdateServiceAgreementAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere serviceavtalene."
        );
      });
  },
});

export const { clearServiceAgreementError } = serviceAgreementSlice.actions;

export const { reducer } = serviceAgreementSlice;

export default serviceAgreementSlice;
